import { appendLevelGuides } from '../lib/level-guides.js';
import { extractTags, formatMarkdownInline, isMarkdownHorizontalRule } from '../lib/text-format.js';
import { renderMermaidBlocks } from '../lib/mermaid-render.js';
import { getOutlineChildren } from './outline-state.js';
import { subtreeToMarkdown } from '../lib/markdown-outline.js';
import { highlightHtmlText } from '../lib/highlight.js';
import { splitSearchTerms, textMatchesAllTerms } from '../lib/search-navigation.js';
import { updateNodeHeaderStyle } from '../lib/node-style.js';
import { makeIconButton, makeNodeMenuButton as buildNodeMenuButton } from '../lib/node-menu.js';
import { depthToneFor } from './theme.js';

function formatNodeText(content) {
  return isMarkdownHorizontalRule(content)
    ? '<hr class="node-horizontal-rule">'
    : formatMarkdownInline(content, { blockLines: true });
}

// 公開画面のメニューは、テンプレートが window に載せた関数で閉じる。
function closePublicMenus() {
  if (typeof window.closePublicNodeMenus === 'function') {
    window.closePublicNodeMenus();
  }
}

function makeNodeMenuButton(label, title, handler, options = {}) {
  return buildNodeMenuButton(label, title, handler, closePublicMenus, options);
}

export function renderPublicOutline({ state, outlineEl, t }) {
  function hasChildren(id) {
    return getOutlineChildren(state, id).length > 0;
  }

  function renderNode(parentEl, id, depth, filterContext, isLastChildPath = []) {
    const node = state.nodes.get(id);
    if (!node) {
      return;
    }

    if (filterContext && !filterContext.visibleIds.has(id)) {
      return;
    }

    const row = document.createElement('div');
    row.className = 'node public-node';
    if (filterContext && filterContext.matchedIds.has(id)) {
      row.classList.add('is-search-match');
    }
    row.classList.toggle('is-active', id === state.activeNodeId);
    row.dataset.id = String(id);
    row.dataset.depthTone = depthToneFor(depth);
    row.style.setProperty('--depth', String(depth));

    const toggle = document.createElement('button');
    toggle.className = 'toggle-button';
    toggle.type = 'button';
    toggle.dataset.id = String(id);

    const iconSpan = document.createElement('span');
    iconSpan.className = 'node-icon';
    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgEl.setAttribute('viewBox', '0 0 24 24');
    svgEl.setAttribute('width', '24');
    svgEl.setAttribute('height', '24');
    const useEl = document.createElementNS('http://www.w3.org/2000/svg', 'use');

    const nodeHasChildren = hasChildren(id);
    row.classList.toggle('is-folder-card', nodeHasChildren);
    row.classList.toggle('is-leaf-card', !nodeHasChildren);
    if (nodeHasChildren) {
      toggle.classList.add('has-children');
      const isCurrentlyOpen = !node.collapsed || (filterContext && filterContext.forceExpandedIds.has(id));
      toggle.classList.toggle('is-open', isCurrentlyOpen);
      toggle.setAttribute('aria-label', isCurrentlyOpen ? t('node_collapse') : t('node_expand'));
      toggle.setAttribute('aria-expanded', isCurrentlyOpen ? 'true' : 'false');
      const isCardFolder = document.body.dataset.layout === 'card-folder';
      useEl.setAttribute(
        'href',
        isCardFolder
          ? isCurrentlyOpen
            ? '#icon-folder-open'
            : '#icon-folder-closed'
          : isCurrentlyOpen
            ? '#icon-open'
            : '#icon-closed'
      );
      toggle.addEventListener('click', () => {
        node.collapsed = isCurrentlyOpen ? 1 : 0;
        render();
      });
    } else {
      toggle.classList.add('is-leaf');
      toggle.setAttribute('aria-label', t('drag_handle'));
      toggle.tabIndex = -1;
      const layout = document.body.dataset.layout;
      useEl.setAttribute('href', layout === 'card-folder' ? '#icon-card' : '#icon-empty');
    }

    svgEl.appendChild(useEl);
    iconSpan.appendChild(svgEl);
    toggle.appendChild(iconSpan);

    const text = document.createElement('div');
    text.className = 'node-text';
    text.dataset.id = String(id);
    text.setAttribute('tabindex', '-1');
    const initialHtml = formatNodeText(node.text);
    text.innerHTML =
      filterContext && filterContext.query ? highlightHtmlText(initialHtml, filterContext.query) : initialHtml;
    text.setAttribute('role', 'textbox');
    text.setAttribute('aria-label', t('node_text'));
    text.spellcheck = false;
    updateNodeHeaderStyle(text, node.text);

    const actions = document.createElement('div');
    actions.className = 'node-actions';

    const menuId = `node-menu-${id}`;
    const menuButton = makeIconButton('︙', t('menu'), (event) => {
      event.stopPropagation();
      if (typeof window.togglePublicNodeMenu === 'function') {
        window.togglePublicNodeMenu(id);
      }
    });
    menuButton.classList.add('node-menu-button');
    menuButton.setAttribute('aria-haspopup', 'menu');
    menuButton.setAttribute('aria-expanded', 'false');
    menuButton.setAttribute('aria-controls', menuId);

    const menu = document.createElement('div');
    menu.id = menuId;
    menu.className = 'node-menu-panel';
    menu.setAttribute('role', 'menu');
    menu.hidden = true;

    let copyBtn = makeNodeMenuButton(
      '📋',
      t('node_copy'),
      async () => {
        try {
          const copyText = nodeHasChildren
            ? subtreeToMarkdown(
                id,
                (nodeId) => state.nodes.get(nodeId),
                (nodeId) => getOutlineChildren(state, nodeId)
              )
            : node.text;
          await navigator.clipboard.writeText(copyText);
          copyBtn.textContent = '✓';
          setTimeout(() => {
            copyBtn.textContent = '📋';
            if (typeof window.closePublicNodeMenus === 'function') {
              window.closePublicNodeMenus();
            }
          }, 1000);
        } catch (err) {
          console.error('Failed to copy text: ', err);
          if (typeof window.closePublicNodeMenus === 'function') {
            window.closePublicNodeMenus();
          }
        }
      },
      { closeOnClick: false }
    );

    const menuItems: HTMLElement[] = [copyBtn];
    const tags = extractTags(node.text);
    if (tags.length > 0) {
      const separator = document.createElement('div');
      separator.className = 'menu-separator';
      menuItems.push(separator);

      tags.forEach((tag) => {
        menuItems.push(
          makeNodeMenuButton('#', `#${tag}`, () => {
            if (window.publicTagBrowser) {
              window.publicTagBrowser.selectedTag = tag;
              window.publicTagBrowser.setOpen(true);
            }
          })
        );
      });
    }
    menu.append(...menuItems);
    actions.append(menuButton, menu);

    row.append(toggle, text, actions);
    appendLevelGuides(row, depth, isLastChildPath);
    parentEl.append(row);

    const isExpanded = !node.collapsed || (filterContext && filterContext.forceExpandedIds.has(id));
    if (isExpanded) {
      const children = getOutlineChildren(state, id);
      const visibleChildren = filterContext
        ? children.filter((childId) => filterContext.visibleIds.has(childId))
        : children;

      visibleChildren.forEach((childId, idx) => {
        const isLast = idx === visibleChildren.length - 1;
        renderNode(parentEl, childId, depth + 1, filterContext, [...isLastChildPath, isLast]);
      });
    }
  }

  function render() {
    const fragment = document.createDocumentFragment();

    const query = state.searchQuery ? state.searchQuery.trim().toLowerCase() : '';
    const searchTerms = splitSearchTerms(query);
    const matchedIds = new Set();
    const visibleIds = new Set();
    const forceExpandedIds = new Set();

    if (query !== '') {
      for (const [id, node] of state.nodes) {
        if (textMatchesAllTerms(node.text, searchTerms)) {
          matchedIds.add(id);
        }
      }
      for (const id of matchedIds) {
        visibleIds.add(id);
        let parentId = state.nodes.get(id)?.parent_id;
        while (parentId !== null && parentId !== undefined) {
          visibleIds.add(parentId);
          forceExpandedIds.add(parentId);
          parentId = state.nodes.get(parentId)?.parent_id;
        }
      }
    }

    const filterContext = query !== '' ? { matchedIds, visibleIds, forceExpandedIds, query } : null;

    const visibleRootIds = filterContext
      ? state.rootIds.filter((id) => filterContext.visibleIds.has(id))
      : state.rootIds;

    visibleRootIds.forEach((id, idx) => {
      const isLast = idx === visibleRootIds.length - 1;
      renderNode(fragment, id, 0, filterContext, [isLast]);
    });
    outlineEl.replaceChildren(fragment);
    renderMermaidBlocks(outlineEl);

    if (state.activeNodeId !== null) {
      const text = outlineEl.querySelector(`.node-text[data-id="${state.activeNodeId}"]`);
      text?.focus();
    }
  }

  render();
}
